/**
 * Which rows of a list the shared table shows when a screen gives it a page size
 * (the one-screen layout, audits/2026-10-29/gui/principles.md §4: a list pages inside
 * its panel instead of growing the page).
 *
 * 🔴 It pages **what was read**, nothing more. The rows are the ones the screen holds —
 * a first page from X plus whatever Load more appended — so the count it reports is a
 * count of rows read, never X's total: X documents `with_total_count` as exclusive with
 * `cursor` (`MORE_BANNER` in campaign-copy.ts), and a screen that pages X has no total.
 *
 * 🔴 A page past the end is clamped to the last one rather than shown empty. The rows
 * can shrink under a page the operator is on — a reload after a delete, another account
 * chosen — and an empty page over a non-empty list would print the screen's empty
 * sentence («No campaigns under this account.») over rows that exist.
 *
 * `ui/Table.tsx` renders this and decides nothing itself, as it does `emptyCellText`.
 */

export interface PageSlice<T> {
	/** The rows on the page shown. */
	rows: T[];
	/** The page shown, from 0, after clamping. */
	page: number;
	/** How many pages the rows read make; 1 for an empty list. */
	pages: number;
	/** The first and last row shown, from 1; both 0 for an empty list. */
	from: number;
	to: number;
	/** Rows read — the list's length, not X's total. */
	read: number;
}

/**
 * No page size, or one that is not a whole number above 0, is no paging: every row,
 * as the table has always rendered them.
 */
export function pageOf<T>(
	rows: readonly T[],
	pageSize: number | undefined,
	page: number,
): PageSlice<T> {
	const read = rows.length;
	if (pageSize === undefined || !Number.isInteger(pageSize) || pageSize <= 0) {
		return { rows: [...rows], page: 0, pages: 1, from: read === 0 ? 0 : 1, to: read, read };
	}
	const pages = Math.max(1, Math.ceil(read / pageSize));
	const asked = Number.isInteger(page) ? page : 0;
	const shown = Math.min(Math.max(asked, 0), pages - 1);
	const start = shown * pageSize;
	const end = Math.min(read, start + pageSize);
	return {
		rows: rows.slice(start, end),
		page: shown,
		pages,
		from: read === 0 ? 0 : start + 1,
		to: end,
		read,
	};
}

/** Where a page stands, as `pageOf` returned it — clamped already. */
type Shown = Pick<PageSlice<unknown>, "page" | "pages">;

/**
 * 🔴 The pager's two steps, taken from the page **shown**, not from the page asked.
 * After the rows shrink, the page asked can sit past the end while `pageOf` shows the
 * last one; a step from the asked number would land past the end again and the press
 * would change nothing (-da on twads#232). Both steps stay inside the pages there are.
 */
export function nextPage(shown: Shown): number {
	return Math.min(shown.page + 1, shown.pages - 1);
}

export function previousPage(shown: Shown): number {
	return Math.max(shown.page - 1, 0);
}

/** The bounds that switch the two buttons off: no step from here would move. */
export function atFirstPage(shown: Shown): boolean {
	return shown.page <= 0;
}

export function atLastPage(shown: Shown): boolean {
	return shown.page >= shown.pages - 1;
}
